<script>
  import { createEventDispatcher, onMount, onDestroy } from 'svelte';
  import { questionsetStore } from '../../stores/questionsetStore';
  
  // Import OpenLayers modules directly (since you're using npm)
  import GeoMap from 'ol/Map';
  import View from 'ol/View';
  import TileLayer from 'ol/layer/Tile';
  import XYZ from 'ol/source/XYZ';
  import VectorLayer from 'ol/layer/Vector';
  import VectorSource from 'ol/source/Vector';
  import Feature from 'ol/Feature';
  import { Point, Circle as CircleGeom } from 'ol/geom';
  import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
  import { fromLonLat, toLonLat } from 'ol/proj';
  
  const dispatch = createEventDispatcher();
  
  export let mapData = null;
  
  let mapContainer;
  let map;
  let vectorLayer;
  let circleFeature;
  let centerFeature;
  
  // Get current data from store
  $: storeData = $questionsetStore;
  
  // Working copy - initialize after store is loaded
  let workingMapData = null;
  
  onMount(() => {
    // Initialize working data after component mounts and store is available
    workingMapData = {
      lat: mapData?.lat || storeData?.map?.lat || 52.5200, // Berlin default
      long: mapData?.long || storeData?.map?.long || 13.4050, // Berlin default
      radiusKm: mapData?.radiusKm || storeData?.map?.radiusKm || 0.25
    };
    
    initializeMap();
  });
  
  onDestroy(() => {
    if (map) {
      map.setTarget(null);
    }
  });
  
  function initializeMap() {
    if (!mapContainer || !workingMapData) return;
    
    // Create vector source and layer for the circle and center point
    const vectorSource = new VectorSource();
    vectorLayer = new VectorLayer({
      source: vectorSource
    });
    
    // Create the map
    map = new GeoMap({
      target: mapContainer,
      layers: [
        new TileLayer({
          source: new XYZ({
            url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
          }),
        }),
        vectorLayer
      ],
      view: new View({
        center: fromLonLat([workingMapData.long, workingMapData.lat]),
        zoom: 15
      })
    });
    
    // Add initial features
    updateMapFeatures();
    
    // Add click event to update center
    map.on('singleclick', function(event) {
      const coordinate = toLonLat(event.coordinate);
      workingMapData = {
        ...workingMapData,
        long: parseFloat(coordinate[0].toFixed(6)),
        lat: parseFloat(coordinate[1].toFixed(6))
      };
      updateMapFeatures();
    });
  }
  
  function updateMapFeatures() {
    if (!vectorLayer || !map || !workingMapData) return;
    
    const vectorSource = vectorLayer.getSource();
    vectorSource.clear();
    
    const centerCoord = [workingMapData.long, workingMapData.lat];
    const centerCoordProj = fromLonLat(centerCoord);
    
    // Create center point
    centerFeature = new Feature({
      geometry: new Point(centerCoordProj),
      type: 'center'
    });
    
    // Create circle
    const radiusInMeters = workingMapData.radiusKm * 1000;
    const circle = new CircleGeom(centerCoordProj, radiusInMeters);
    circleFeature = new Feature({
      geometry: circle,
      type: 'circle'
    });
    
    // Different styles for center point and circle
    centerFeature.setStyle(new Style({
      image: new CircleStyle({
        radius: 8,
        fill: new Fill({
          color: '#ef4444'
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        })
      })
    }));
    
    circleFeature.setStyle(new Style({
      fill: new Fill({
        color: 'rgba(59, 130, 246, 0.3)'
      }),
      stroke: new Stroke({
        color: '#3b82f6',
        width: 2
      })
    }));
    
    vectorSource.addFeatures([circleFeature, centerFeature]);
    
    // Center the view on the new location
    map.getView().setCenter(centerCoordProj);
  }
  
  function handleRadiusChange(event) {
    const newRadius = parseFloat(event.target.value);
    if (!isNaN(newRadius) && newRadius > 0) {
      workingMapData = {
        ...workingMapData,
        radiusKm: newRadius
      };
      updateMapFeatures();
    }
  }
  
  function handleLatChange(event) {
    const newLat = parseFloat(event.target.value);
    if (!isNaN(newLat) && newLat >= -90 && newLat <= 90) {
      workingMapData = {
        ...workingMapData,
        lat: newLat
      };
      updateMapFeatures();
    }
  }
  
  function handleLongChange(event) {
    const newLong = parseFloat(event.target.value);
    if (!isNaN(newLong) && newLong >= -180 && newLong <= 180) {
      workingMapData = {
        ...workingMapData,
        long: newLong
      };
      updateMapFeatures();
    }
  }
  
  function saveMap() {
    // Update the store - only at root level data.map
    questionsetStore.update(data => {
      const newData = { ...data };
      
      // Add map data at root level only
      newData.map = { ...workingMapData };
      
      return newData;
    });
    
    // Also dispatch for any parent component listening
    dispatch('save', workingMapData);
    dispatch('close');
  }
  
  function cancelEdit() {
    dispatch('close');
  }
  
  function getCurrentLocation() {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser');
      return;
    }

    // Find the button that was clicked
    const button = document.querySelector('.current-location-btn');
    if (!button) return;
    
    // Show loading state
    const originalText = button.textContent;
    button.textContent = 'Getting location...';
    button.disabled = true;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        workingMapData = {
          ...workingMapData,
          lat: parseFloat(position.coords.latitude.toFixed(6)),
          long: parseFloat(position.coords.longitude.toFixed(6))
        };
        updateMapFeatures();
        
        // Restore button
        button.textContent = originalText;
        button.disabled = false;
      },
      (error) => {
        let errorMessage = 'Could not get your current location';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }
        
        alert(errorMessage + ': ' + error.message);
        
        // Restore button
        button.textContent = originalText;
        button.disabled = false;
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }
  
  function setQuickLocation(lat, long) {
    workingMapData = { 
      ...workingMapData, 
      lat: lat, 
      long: long 
    };
    updateMapFeatures();
  }
  
  // Function to get current map data from store (only checking data.map)
  function getCurrentMapFromStore() {
    return storeData?.map || null;
  }
</script>

<div class="modal-overlay" on:click={cancelEdit}>
  <div class="modal-content" on:click|stopPropagation>
    <div class="modal-header">
      <h3>Configure Map Location</h3>
      <p>Click on the map to set the center point, or enter coordinates manually</p>
      {#if getCurrentMapFromStore()}
        <p class="current-data">
          Current: {getCurrentMapFromStore().lat}, {getCurrentMapFromStore().long} 
          ({getCurrentMapFromStore().radiusKm}km)
        </p>
      {:else}
        <p class="no-data">No map data currently configured</p>
      {/if}
    </div>
    
    <div class="map-editor">
      <div class="map-container" bind:this={mapContainer}></div>
      
      <div class="map-controls">
        <div class="control-row">
          <label>Latitude:</label>
          <input 
            type="number" 
            step="0.000001"
            min="-90"
            max="90"
            value={workingMapData?.lat || ''}
            on:input={handleLatChange}
            disabled={!workingMapData}
          />
        </div>
        
        <div class="control-row">
          <label>Longitude:</label>
          <input 
            type="number" 
            step="0.000001"
            min="-180"
            max="180"
            value={workingMapData?.long || ''}
            on:input={handleLongChange}
            disabled={!workingMapData}
          />
        </div>
        
        <div class="control-row">
          <label>Radius (km):</label>
          <input 
            type="number" 
            step="0.01"
            min="0.01"
            max="100"
            value={workingMapData?.radiusKm || ''}
            on:input={handleRadiusChange}
            disabled={!workingMapData}
          />
        </div>
        
        <button class="btn btn-secondary current-location-btn" on:click={getCurrentLocation} disabled={!workingMapData}>
          📍 Use Current Location
        </button>
        
        <div class="preview-values">
          <h4>Preview Values:</h4>
          {#if workingMapData}
            <p><strong>Lat:</strong> {workingMapData.lat}</p>
            <p><strong>Long:</strong> {workingMapData.long}</p>
            <p><strong>Radius:</strong> {workingMapData.radiusKm}km</p>
          {:else}
            <p>Loading...</p>
          {/if}
        </div>
        
        <div class="coordinate-help">
          <h4>Quick Locations:</h4>
          <button class="btn btn-mini" on:click={() => setQuickLocation(52.5200, 13.4050)} disabled={!workingMapData}>
            🇩🇪 Berlin
          </button>
          <button class="btn btn-mini" on:click={() => setQuickLocation(51.5074, -0.1278)} disabled={!workingMapData}>
            🇬🇧 London
          </button>
        </div>
      </div>
    </div>
    
    <div class="modal-actions">
      <button class="btn" on:click={cancelEdit}>Cancel</button>
      <button class="btn btn-primary" on:click={saveMap} disabled={!workingMapData}>Save Map</button>
    </div>
  </div>
</div>

<style lang="scss">
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
  }
  
  .modal-content {
    background: #f6f6f6;
    border-radius: 10px;
    width: 80%;
    max-width: 800px;
    max-height: 90%;
    border: 4px solid #d4d3bc;
    display: flex;
    flex-direction: column;
  }
  
  .modal-header {
    padding: 20px;
    border-bottom: 2px solid #d4d3bc;
    
    h3 {
      margin: 0 0 8px 0;
    }
    
    p {
      margin: 0 0 4px 0;
      font-size: 0.85rem;
      color: #6b7280;
    }
    
    .current-data {
      color: #059669 !important;
      font-weight: bold;
    }
    
    .no-data {
      color: #f59e0b !important;
      font-style: italic;
    }
  }
  
  .map-editor {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 20px;
    padding: 20px;
    flex: 1;
    min-height: 500px;
  }
  
  .map-container {
    background: #e5e7eb;
    border: 2px solid #d4d3bc;
    border-radius: 8px;
    min-height: 400px;
    width: 100%;
  }
  
  .map-controls {
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 20px;
    background: #f3f2e3;
    border-radius: 8px;
    border: 2px solid #d4d3bc;
    
    .control-row {
      display: flex;
      flex-direction: column;
      gap: 5px;
      
      label {
        font-weight: bold;
        font-size: 0.75rem;
        color: #464b4b;
      }
      
      input {
        padding: 8px;
        border: 2px solid #d4d3bc;
        border-radius: 5px;
        background: #fff;
        font-size: 0.75rem;
        
        &:focus {
          outline: none;
          border-color: #3b82f6;
        }
      }
    }
    
    .preview-values {
      background: #f0f9ff;
      padding: 10px;
      border-radius: 5px;
      border: 1px solid #0ea5e9;
      
      h4 {
        margin: 0 0 8px 0;
        font-size: 0.75rem;
        color: #0369a1;
      }
      
      p {
        margin: 2px 0;
        font-size: 0.7rem;
        color: #0369a1;
      }
    }
    
    .coordinate-help {
      background: #fefce8;
      padding: 10px;
      border-radius: 5px;
      border: 1px solid #eab308;
      
      h4 {
        margin: 0 0 8px 0;
        font-size: 0.75rem;
        color: #a16207;
      }
      
      .btn-mini {
        font-size: 0.7rem;
        padding: 4px 8px;
        margin: 2px;
        background: #eab308;
        color: white;
        border: none;
        border-radius: 3px;
        cursor: pointer;
        
        &:hover {
          background: #ca8a04;
        }
      }
    }
  }
  
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 20px;
    border-top: 2px solid #d4d3bc;
  }
  
  .btn {
    padding: 8px 16px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: bold;
    transition: all 0.2s ease;
    
    &.btn-primary {
      background: #3b82f6;
      color: white;
      
      &:hover {
        background: #2563eb;
      }
    }
    
    &.btn-secondary {
      background: #10b981;
      color: white;
      width: 100%;
      
      &:hover {
        background: #059669;
      }
      
      &:disabled {
        background: #9ca3af;
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
    
    &:not(.btn-primary):not(.btn-secondary) {
      background: #e5e7eb;
      color: #374151;
      
      &:hover {
        background: #d1d5db;
      }
    }
  }
  
  @media (max-width: 768px) {
    .modal-content {
      width: 95%;
      height: 90%;
    }
    
    .map-editor {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr auto;
    }
    
    .map-container {
      min-height: 300px;
    }
  }
</style>